import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/style.css";

import api, { IMAGE_BASE_URL } from "../api/Api.js";
import { useLanguage } from "../context/LanguageContext";

const CarDetails = () => {
  const navigate = useNavigate();
  const { carmodelId: carmodelIdParam } = useParams();
  const { language, t } = useLanguage();

  // ============================================================
  // STATE
  // ============================================================

  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showMore, setShowMore] = useState(false);

  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const [pickupLocation, setPickupLocation] = useState("");
  const [returnLocation, setReturnLocation] = useState("");

  const [pickupDate, setPickupDate] = useState("");
  const [returnDate, setReturnDate] = useState("");

  const [pickupTime, setPickupTime] = useState("10:00");
  const [returnTime, setReturnTime] = useState("10:00");

  const [bookingError, setBookingError] = useState("");
  const [bookingLoading, setBookingLoading] = useState(false);

  // ============================================================
  // IMPORTANT CONTENT
  // ============================================================

  const [importantContents, setImportantContents] = useState({
    page9: {
      cont_title: "",
      cont_details: "",
    },
    page10: {
      cont_title: "",
      cont_details: "",
    },
    page11: {
      cont_title: "",
      cont_details: "",
    },
  });

  const [loadingImportantContents, setLoadingImportantContents] =
    useState(true);

  // ============================================================
  // TRANSLATION
  // ============================================================

  const tt = (key, fallback = "") => {
    try {
      return t(`cardetails.${key}`, fallback);
    } catch (err) {
      return fallback;
    }
  };

  // ============================================================
  // LANGUAGE ID
  // ============================================================

  const getLanguageId = () => {
    if (!language) {
      return 1;
    }

    if (typeof language === "object") {
      return (
        Number(
          language.lang_id ||
            language.language_id ||
            language.id
        ) || 1
      );
    }

    return Number(language) || 1;
  };

  const languageId = getLanguageId();

  // ============================================================
  // TODAY
  // ============================================================

  const getToday = () => {
    const date = new Date();

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const today = getToday();

  // ============================================================
  // RESOLVE CAR MODEL ID
  // ============================================================

  const resolveCarmodelId = () => {
    if (carmodelIdParam) {
      const id = Number(carmodelIdParam);

      if (!Number.isNaN(id) && id > 0) {
        return id;
      }
    }

    try {
      const bookingCriteria =
        sessionStorage.getItem("bookingCriteria");

      if (bookingCriteria) {
        const parsed = JSON.parse(bookingCriteria);

        const id =
          parsed?.selectedCarId ||
          parsed?.carmodel_id ||
          parsed?.car_id;

        if (id) {
          const numericId = Number(id);

          if (!Number.isNaN(numericId) && numericId > 0) {
            return numericId;
          }
        }
      }
    } catch (err) {
      console.error("Unable to read bookingCriteria:", err);
    }

    try {
      const selectedCar =
        sessionStorage.getItem("selectedCar");

      if (selectedCar) {
        const parsed = JSON.parse(selectedCar);

        const id =
          parsed?.carmodel_id ||
          parsed?.car_id ||
          parsed?.id;

        if (id) {
          const numericId = Number(id);

          if (!Number.isNaN(numericId) && numericId > 0) {
            return numericId;
          }
        }
      }
    } catch (err) {
      console.error("Unable to read selectedCar:", err);
    }

    return null;
  };

  // ============================================================
  // IMAGE
  // ============================================================

  const getCarImage = (image) => {
    if (!image) {
      return "/images/car_34.jpg";
    }

    let imagePath = String(image).trim();

    if (!imagePath) {
      return "/images/car_34.jpg";
    }

    if (
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://")
    ) {
      return imagePath;
    }

    imagePath = imagePath.replace(/^\/+/, "");

    const imageBase =
      IMAGE_BASE_URL || "http://localhost:5555";

    return `${imageBase}/${imagePath}`;
  };

  // ============================================================
  // CLEAN DATABASE HTML
  // ============================================================

  const cleanContentHtml = (html) => {
    if (!html) {
      return "";
    }

    return String(html)
      .replace(/<li>\s*<\/li>/gi, "")
      .replace(
        /<li>(?:\s|&nbsp;|<br\s*\/?>)*<\/li>/gi,
        ""
      )
      .replace(
        /<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi,
        ""
      )
      .replace(
        /<div>(?:\s|&nbsp;|<br\s*\/?>)*<\/div>/gi,
        ""
      );
  };

  // ============================================================
  // FETCH CONTENT PAGE
  // ============================================================

  const fetchContentPage = async (pageId, langId) => {
    try {
      const response = await api.get("/contents/content", {
        params: {
          page_id: pageId,
          lang_id: langId,
        },
      });

      const result = response.data;

      if (result && result.success && result.data) {
        if (Array.isArray(result.data)) {
          return result.data[0] || null;
        }

        return result.data;
      }

      return null;
    } catch (err) {
      console.error(
        `ERROR FETCHING CONTENT PAGE ${pageId}:`,
        err
      );

      return null;
    }
  };

  // ============================================================
  // FETCH IMPORTANT CONTENT
  // ============================================================

  useEffect(() => {
    let cancelled = false;

    const loadImportantContents = async () => {
      setLoadingImportantContents(true);

      const [page9, page10, page11] =
        await Promise.all([
          fetchContentPage(9, languageId),
          fetchContentPage(10, languageId),
          fetchContentPage(11, languageId),
        ]);

      if (cancelled) {
        return;
      }

      setImportantContents({
        page9: {
          cont_title: page9?.cont_title || "",
          cont_details: page9?.cont_details || "",
        },

        page10: {
          cont_title: page10?.cont_title || "",
          cont_details: page10?.cont_details || "",
        },

        page11: {
          cont_title: page11?.cont_title || "",
          cont_details: page11?.cont_details || "",
        },
      });

      setLoadingImportantContents(false);
    };

    loadImportantContents();

    return () => {
      cancelled = true;
    };
  }, [languageId]);

  // ============================================================
  // FETCH CAR DETAILS
  // ============================================================

  useEffect(() => {
    fetchCarDetails();
  }, [languageId, carmodelIdParam]);

  const fetchCarDetails = async () => {
    try {
      setLoading(true);
      setError("");
      setShowMore(false);

      const carmodelId = resolveCarmodelId();

      if (!carmodelId) {
        setCar(null);

        setError(
          tt(
            "noCarSelected",
            "No car selected."
          )
        );

        setLoading(false);
        return;
      }

      console.log("FETCHING CAR DETAILS");
      console.log("Car Model ID:", carmodelId);
      console.log("Language ID:", languageId);

      const response = await api.get(
        `cars/${carmodelId}/details`,
        {
          params: {
            langid: languageId,
          },
        }
      );

      console.log("CAR DETAILS RESPONSE:", response.data);

      if (response.data?.success) {
        const carData = response.data.data || null;

        console.log("CAR DATA:", carData);
        console.log(
          "INCLUDED ITEMS:",
          carData?.includedItems
        );

        setCar(carData);
      } else {
        setCar(null);

        setError(
          tt(
            "carNotFound",
            "Car not found."
          )
        );
      }
    } catch (err) {
      console.error("CAR DETAILS API ERROR:", err);
      console.error("STATUS:", err.response?.status);
      console.error("RESPONSE:", err.response?.data);

      setCar(null);

      setError(
        tt(
          "loadError",
          "Unable to load car details."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // FETCH LOCATIONS
  // ============================================================

  useEffect(() => {
    fetchLocations();
  }, [languageId]);

  const fetchLocations = async () => {
    try {
      setLocationsLoading(true);

      const response = await api.get(
        "/locations/getLocations",
        {
          params: {
            city_id: 1,
            lang_id: languageId,
          },
        }
      );

      console.log(
        "LOCATIONS RESPONSE:",
        response.data
      );

      let locationData = [];

      if (Array.isArray(response.data)) {
        locationData = response.data;
      } else if (
        Array.isArray(response.data?.data)
      ) {
        locationData = response.data.data;
      } else if (
        Array.isArray(response.data?.locations)
      ) {
        locationData = response.data.locations;
      }

      setLocations(locationData);
    } catch (err) {
      console.error("LOCATION API ERROR:", err);
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  };

  // ============================================================
  // LOCATION HELPERS
  // ============================================================

  const getLocationId = (location) => {
    if (!location) {
      return "";
    }

    return (
      location.location_id ||
      location.locationid ||
      location.station_id ||
      location.stationid ||
      location.loc_id ||
      location.id ||
      ""
    );
  };

  const getLocationName = (location) => {
    if (!location) {
      return "";
    }

    return (
      location.location_name ||
      location.locationname ||
      location.station_name ||
      location.stationname ||
      location.loc_name ||
      location.name ||
      location.title ||
      ""
    );
  };

  // ============================================================
  // YES / NO
  // ============================================================

  const yesNo = (value) => {
    if (
      value === "Yes" ||
      value === "yes" ||
      value === 1 ||
      value === "1" ||
      value === true
    ) {
      return tt("yes", "Yes");
    }

    return tt("no", "No");
  };

  // ============================================================
  // FEATURES
  // ============================================================

  const getFeatureValue = (item) => {
    if (
      item === undefined ||
      item === null
    ) {
      return "";
    }

    if (
      typeof item === "string" ||
      typeof item === "number"
    ) {
      return String(item).trim();
    }

    if (typeof item === "object") {
      return String(
        item.item_name ||
          item.name ||
          item.mldynsec_val ||
          item.value ||
          item.title ||
          item.label ||
          item.description ||
          ""
      ).trim();
    }

    return "";
  };

  const getAllFeatures = (carData) => {
    let source = [];

    if (
      Array.isArray(carData?.includedItems) &&
      carData.includedItems.length > 0
    ) {
      source = carData.includedItems;
    } else if (
      Array.isArray(carData?.features) &&
      carData.features.length > 0
    ) {
      source = carData.features;
    } else if (
      Array.isArray(carData?.included_items) &&
      carData.included_items.length > 0
    ) {
      source = carData.included_items;
    } else if (
      Array.isArray(carData?.carFeatures) &&
      carData.carFeatures.length > 0
    ) {
      source = carData.carFeatures;
    } else if (
      Array.isArray(carData?.car_features) &&
      carData.car_features.length > 0
    ) {
      source = carData.car_features;
    }

    return source
      .map(getFeatureValue)
      .filter(Boolean);
  };

  // ============================================================
  // SELECTED CAR ID
  // ============================================================

  const getSelectedCarId = () => {
    if (!car) {
      return resolveCarmodelId();
    }

    return (
      car.carmodel_id ||
      car.carmodelId ||
      car.car_model_id ||
      car.car_id ||
      car.id ||
      resolveCarmodelId()
    );
  };

  // ============================================================
  // CATEGORY
  // ============================================================

  const getCarCategoryId = () => {
    if (!car) {
      return "";
    }

    return (
      car.cat_id ||
      car.category_id ||
      car.catid ||
      car.categoryId ||
      ""
    );
  };

  // ============================================================
  // DRIVER AGE
  // ============================================================

  const getDriverAge = () => {
    if (!car) {
      return "";
    }

    return (
      car.driverAge ||
      car.driver_age ||
      ""
    );
  };

  // ============================================================
  // CONTINUE
  // ============================================================

  const handleContinue = async (event) => {
    event.preventDefault();

    setBookingError("");

    if (!pickupLocation) {
      setBookingError(
        tt(
          "selectPickupLocation",
          "Please select pickup location."
        )
      );
      return;
    }

    if (!returnLocation) {
      setBookingError(
        tt(
          "selectReturnLocation",
          "Please select return location."
        )
      );
      return;
    }

    if (!pickupDate) {
      setBookingError(
        tt(
          "selectPickupDate",
          "Please select pickup date."
        )
      );
      return;
    }

    if (!returnDate) {
      setBookingError(
        tt(
          "selectReturnDate",
          "Please select return date."
        )
      );
      return;
    }

    if (!pickupTime) {
      setBookingError(
        tt(
          "selectPickupTime",
          "Please select pickup time."
        )
      );
      return;
    }

    if (!returnTime) {
      setBookingError(
        tt(
          "selectReturnTime",
          "Please select return time."
        )
      );
      return;
    }

    const pickupDateObject =
      new Date(`${pickupDate}T00:00:00`);

    const returnDateObject =
      new Date(`${returnDate}T00:00:00`);

    if (returnDateObject < pickupDateObject) {
      setBookingError(
        tt(
          "invalidDates",
          "Return date cannot be before pickup date."
        )
      );
      return;
    }

    if (
      pickupDate === returnDate &&
      returnTime <= pickupTime
    ) {
      setBookingError(
        tt(
          "invalidTime",
          "Return time must be after pickup time."
        )
      );
      return;
    }

    const selectedCarId = getSelectedCarId();

    if (!selectedCarId) {
      setBookingError(
        tt(
          "noCarSelected",
          "No car selected."
        )
      );
      return;
    }

    const pickupTimeParts = pickupTime.split(":");
    const returnTimeParts = returnTime.split(":");

    const pickupHour =
      pickupTimeParts[0] || "10";

    const pickupMinute =
      pickupTimeParts[1] || "00";

    const returnHour =
      returnTimeParts[0] || "10";

    const returnMinute =
      returnTimeParts[1] || "00";

    const criteria = {
      cmbPickLocation: String(pickupLocation),
      pickdate: pickupDate,
      cmbpickHour: pickupHour,
      cmbpickmin: pickupMinute,

      cmbDropLocation: String(returnLocation),
      returndate: returnDate,
      cmbreturnHour: returnHour,
      cmbreturnmin: returnMinute,

      cmbcartype: getCarCategoryId(),
      cmbdrvage: getDriverAge(),

      clientid: "",
      clientemail: "",
      selectedlanguage: languageId,

      Editourcar: "",

      selectedCarId: Number(selectedCarId),
    };

    console.log(
      "BOOKING CRITERIA:",
      criteria
    );

    try {
      setBookingLoading(true);

      sessionStorage.setItem(
        "bookingCriteria",
        JSON.stringify(criteria)
      );

      if (car) {
        sessionStorage.setItem(
          "selectedCar",
          JSON.stringify(car)
        );
      }

      sessionStorage.setItem(
        "selectedCarId",
        String(selectedCarId)
      );

      navigate("/bookacarthree");
    } catch (err) {
      console.error(
        "BOOKING NAVIGATION ERROR:",
        err
      );

      setBookingError(
        tt(
          "bookingError",
          "Unable to continue booking. Please try again."
        )
      );
    } finally {
      setBookingLoading(false);
    }
  };

  // ============================================================
  // PREVIOUS
  // ============================================================

  const handleGoPrevious = () => {
    navigate(-1);
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">
            {tt("loading", "Loading...")}
          </span>
        </div>
      </div>
    );
  }

  // ============================================================
  // ERROR
  // ============================================================

  if (error || !car) {
    return (
      <div className="p20">
        <div className="container container2">
          <div
            className="col-md-12 main-content"
            style={{ float: "left" }}
          >
            <div className="text-center py-5">
              <h4>
                {error ||
                  tt(
                    "carNotFound",
                    "Car not found."
                  )}
              </h4>

              <button
                type="button"
                className="btn btn-secondary1 py-3 px-4 mt-3"
                onClick={handleGoPrevious}
              >
                «{" "}
                {tt(
                  "goPrevious",
                  "Go Previous"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // NORMALIZE FEATURES
  // ============================================================

  const normalizedFeatures =
    Array.isArray(car.includedItems)
      ? car.includedItems
          .map((item) => {
            if (typeof item === "string") {
              return item;
            }

            if (
              item &&
              typeof item === "object"
            ) {
              return (
                item.item_name ||
                item.itemName ||
                item.name ||
                item.included_item_name ||
                item.includedItemName ||
                item.title ||
                ""
              );
            }

            return "";
          })
          .filter(Boolean)
      : [];

  const firstFeatures =
    normalizedFeatures.slice(0, 4);

  const extraFeatures =
    normalizedFeatures.slice(4);

  // ============================================================
  // CAR BASIC DATA
  // ============================================================

  const carTitle =
    car.name ||
    car.model ||
    [car.brand, car.model]
      .filter(Boolean)
      .join(" ") ||
    tt("car", "Car");

  const carSubtitle =
    car.category ||
    car.category_name ||
    car.cat_name ||
    "";

  const price =
    car.pricePerDay ??
    car.price_per_day ??
    car.price ??
    null;

  const image = getCarImage(car.image);

  // ============================================================
  // IMPORTANT CONTENT
  // ============================================================

  const page9Title =
    importantContents.page9.cont_title ||
    "Also Important to know";

  const page9Details =
    cleanContentHtml(
      importantContents.page9.cont_details
    );

  const page10Title =
    importantContents.page10.cont_title ||
    "Attention!!";

  const page10Details =
    cleanContentHtml(
      importantContents.page10.cont_details
    );

  const page11Title =
    importantContents.page11.cont_title ||
    "Enjoy your holidays";

  const page11Details =
    cleanContentHtml(
      importantContents.page11.cont_details
    );

  // ============================================================
  // OPEN MODAL
  // ============================================================

  const openDetailsModal = () => {
    setShowDetailsModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    document.body.style.overflow = "";
  };

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <>
      {/* ======================================================
          BOOKING ENGINE
      ====================================================== */}

      <div
        className="booking-engine-wrapper"
        style={{
          width: "100%",
          display: "block",
          position: "relative",
          zIndex: 20,
        }}
      >
        <div
          className="hero-wrap ftco-degree-bg"
          style={{
            width: "100%",
            minHeight: "410px",
            height: "auto",
            display: "block",
          }}
        >
          <div className="container">
            <div
              className="
                row
                no-gutters
                slider-text
                justify-content-center
                align-items-center
              "
              style={{
                height: "auto",
                minHeight: "410px",
              }}
            >
              <div className="col-lg-8">
                <div className="text w-100 text-center">
                  <div className="col-md-12 d-flex align-items-center">
                    <form
                      className="request-form bg-primary"
                      onSubmit={handleContinue}
                      style={{
                        width: "100%",
                        display: "block",
                      }}
                    >
                      {/* PICKUP */}

                      <div
                        className="col-md-6"
                        style={{
                          float: "left",
                          padding: 0,
                        }}
                      >
                        <div
                          className="col-lg-12"
                          style={{
                            float: "left",
                            padding: 0,
                            textAlign: "left",
                          }}
                        >
                          <label className="label">
                            {tt(
                              "pickupInformation",
                              "Pickup Information"
                            )}
                          </label>
                        </div>

                        <div
                          className="col-lg-12"
                          style={{
                            float: "left",
                            padding: 0,
                          }}
                        >
                          <div
                            className="col-lg-12 form-group"
                            style={{
                              float: "left",
                              padding:
                                "0 9px 0 0",
                            }}
                          >
                            <div className="selectParent">
                              <select
                                className="form-control select"
                                value={pickupLocation}
                                onChange={(e) => {
                                  setPickupLocation(
                                    e.target.value
                                  );
                                  setBookingError("");
                                }}
                                disabled={
                                  locationsLoading ||
                                  bookingLoading
                                }
                              >
                                <option value="">
                                  {locationsLoading
                                    ? tt(
                                        "loading",
                                        "Loading..."
                                      )
                                    : tt(
                                        "selectLocation",
                                        "Select location"
                                      )}
                                </option>

                                {locations.map(
                                  (
                                    location,
                                    index
                                  ) => {
                                    const id =
                                      getLocationId(
                                        location
                                      );

                                    const name =
                                      getLocationName(
                                        location
                                      );

                                    return (
                                      <option
                                        key={
                                          id ||
                                          `pickup-${index}`
                                        }
                                        value={id || ""}
                                      >
                                        {name}
                                      </option>
                                    );
                                  }
                                )}
                              </select>
                            </div>
                          </div>
                        </div>

                        <div
                          className="col-lg-12"
                          style={{
                            float: "left",
                            padding: 0,
                          }}
                        >
                          <div
                            className="col-lg-6 form-group"
                            style={{
                              float: "left",
                              padding:
                                "0 9px 0 0",
                            }}
                          >
                            <input
                              type="date"
                              className="calendar form-control"
                              value={pickupDate}
                              min={today}
                              disabled={bookingLoading}
                              onChange={(e) => {
                                const value =
                                  e.target.value;

                                setPickupDate(value);

                                if (
                                  returnDate &&
                                  value > returnDate
                                ) {
                                  setReturnDate("");
                                }

                                setBookingError("");
                              }}
                            />
                          </div>

                          <div
                            className="col-lg-6 form-group"
                            style={{
                              float: "left",
                              padding:
                                "0 9px 0 0",
                            }}
                          >
                            <input
                              type="time"
                              className="arrow form-control"
                              value={pickupTime}
                              disabled={bookingLoading}
                              onChange={(e) => {
                                setPickupTime(
                                  e.target.value
                                );
                                setBookingError("");
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* RETURN */}

                      <div
                        className="col-md-6"
                        style={{
                          float: "left",
                          padding: 0,
                        }}
                      >
                        <div
                          className="col-lg-12"
                          style={{
                            float: "left",
                            padding: 0,
                            textAlign: "left",
                          }}
                        >
                          <label className="label">
                            {tt(
                              "returnInformation",
                              "Return Information"
                            )}
                          </label>
                        </div>

                        <div
                          className="col-lg-12"
                          style={{
                            float: "left",
                            padding: 0,
                          }}
                        >
                          <div
                            className="col-lg-12 form-group"
                            style={{
                              float: "left",
                              padding:
                                "0 9px 0 0",
                            }}
                          >
                            <div className="selectParent">
                              <select
                                className="form-control select"
                                value={returnLocation}
                                onChange={(e) => {
                                  setReturnLocation(
                                    e.target.value
                                  );
                                  setBookingError("");
                                }}
                                disabled={
                                  locationsLoading ||
                                  bookingLoading
                                }
                              >
                                <option value="">
                                  {locationsLoading
                                    ? tt(
                                        "loading",
                                        "Loading..."
                                      )
                                    : tt(
                                        "selectLocation",
                                        "Select location"
                                      )}
                                </option>

                                {locations.map(
                                  (
                                    location,
                                    index
                                  ) => {
                                    const id =
                                      getLocationId(
                                        location
                                      );

                                    const name =
                                      getLocationName(
                                        location
                                      );

                                    return (
                                      <option
                                        key={
                                          id ||
                                          `return-${index}`
                                        }
                                        value={id || ""}
                                      >
                                        {name}
                                      </option>
                                    );
                                  }
                                )}
                              </select>
                            </div>
                          </div>
                        </div>

                        <div
                          className="col-lg-12"
                          style={{
                            float: "left",
                            padding: 0,
                          }}
                        >
                          <div
                            className="col-lg-6 form-group"
                            style={{
                              float: "left",
                              padding:
                                "0 9px 0 0",
                            }}
                          >
                            <input
                              type="date"
                              className="calendar form-control"
                              value={returnDate}
                              min={
                                pickupDate || today
                              }
                              disabled={bookingLoading}
                              onChange={(e) => {
                                setReturnDate(
                                  e.target.value
                                );
                                setBookingError("");
                              }}
                            />
                          </div>

                          <div
                            className="col-lg-6 form-group"
                            style={{
                              float: "left",
                              padding:
                                "0 9px 0 0",
                            }}
                          >
                            <input
                              type="time"
                              className="arrow form-control"
                              value={returnTime}
                              disabled={bookingLoading}
                              onChange={(e) => {
                                setReturnTime(
                                  e.target.value
                                );
                                setBookingError("");
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="clearfix"></div>

                      {/* ERROR */}

                      {bookingError && (
                        <div
                          className="col-md-12"
                          style={{
                            padding: "5px 0",
                            color: "#fff",
                            textAlign: "center",
                            fontWeight: 500,
                          }}
                        >
                          {bookingError}
                        </div>
                      )}

                      {/* BUTTONS */}

                      <div
                        className="col-md-12"
                        style={{
                          padding: 0,
                          float: "left",
                        }}
                      >
                        <div
                          className="form-group col-md-6"
                          style={{
                            float: "left",
                            padding:
                              "0 9px 0 0",
                          }}
                        >
                          <button
                            type="submit"
                            className="btn btn-secondary1 "
                            disabled={
                              bookingLoading ||
                              locationsLoading
                            }
                            style={{
                              width: "100%",
                            }}
                          >
                            {bookingLoading
                              ? tt(
                                  "loading",
                                  "Loading..."
                                )
                              : tt(
                                  "continue",
                                  "Continue"
                                )}
                          </button>
                        </div>

                        <div
                          className="col-md-6"
                          style={{
                            float: "left",
                            padding:
                              "0 9px 0 0",
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-secondary2 py-3 px-4"
                            disabled={bookingLoading}
                            style={{
                              width: "100%",
                            }}
                            onClick={() =>
                              navigate("/login")
                            }
                          >
                            {tt(
                              "regularClientLogin",
                              "Regular client Login"
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="clearfix"></div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="clearfix"></div>

      {/* ======================================================
          CAR DETAILS
      ====================================================== */}

      <div className="p20">
        <div className="p0 container container2">
          <div
            className="col-md-12 main-content"
            style={{ float: "left" }}
          >
            <div className="rental-card">
              <div className="card-main-content">

                {/* TITLE */}

                <div
                  className="col-xs-12 col-sm-4 col-md-9 col-lg-9"
                  style={{ float: "left" }}
                >
                  <h2 className="car-title">
                    {carTitle}
                  </h2>

                  <div className="car-subtitle">
                    {carSubtitle}
                  </div>
                </div>

                {/* PRICE */}

                <div
                  className="col-xs-12 col-sm-4 col-md-3 col-lg-3"
                  style={{ float: "left" }}
                >
                  <div className="price-container">
                    <div className="price-label">
                      {tt(
                        "perDay",
                        "(Per day)"
                      )}
                    </div>

                    <div className="main-price">
                      {price !== null
                        ? `${Number(price).toFixed(
                            2
                          )} €`
                        : tt(
                            "contactForPrice",
                            "Contact for price"
                          )}
                    </div>
                  </div>
                </div>

                <div className="clearfix"></div>

                {/* IMAGE */}

                <div
                  className="col-xs-12 col-sm-4 col-md-4 col-lg-4"
                  style={{ float: "left" }}
                >
                  <div className="image-container">
                    <img
                      src={image}
                      alt={carTitle}
                      className="car-img"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src =
                          "/images/car_34.jpg";
                      }}
                    />
                  </div>
                </div>

                {/* FEATURES */}

                <div
                  className="col-xs-12 col-sm-4 col-md-4 col-lg-4"
                  style={{ float: "left" }}
                >
                  <div className="features-container">

                    <div className="specs-row">
                      <span className="spec-tag">
                        👤 {car.passengers || 0}
                      </span>

                      <span className="spec-tag">
                        🧳 {car.luggage || 0}
                      </span>

                      <span className="spec-tag">
                        ❄️{" "}
                        {yesNo(
                          car.airConditioning
                        )}
                      </span>

                      <span className="spec-tag">
                        ⚙️{" "}
                        {car.transmission ||
                          tt(
                            "manual",
                            "Manual"
                          )}
                      </span>
                    </div>

                    {firstFeatures.map(
                      (feature, index) => (
                        <div
                          className="feature-item"
                          key={`first-${index}`}
                        >
                          <span className="feature-icon">
                            <img
                              src="/images/tickm.png"
                              alt=""
                              style={{
                                width: "100%",
                                margin: 0,
                              }}
                            />
                          </span>

                          <strong>
                            {feature}
                          </strong>
                        </div>
                      )
                    )}

                    {extraFeatures.length > 0 && (
                      <>
                        <div
                          className="modify"
                          style={{
                            display: "block",
                            width: "100%",
                            marginTop: "10px",
                          }}
                        >
                          <button
                            type="button"
                            className="showmore"
                            onClick={() =>
                              setShowMore(
                                (prev) => !prev
                              )
                            }
                          >
                            {showMore
                              ? tt(
                                  "showLess",
                                  "Show Less"
                                )
                              : `${tt(
                                  "show",
                                  "Show"
                                )} ${
                                  extraFeatures.length
                                } ${tt(
                                  "more",
                                  "More"
                                )}`}
                          </button>
                        </div>

                        {showMore && (
                          <div
                            className="test1"
                            style={{
                              display: "block",
                              width: "100%",
                              height: "auto",
                              maxHeight: "none",
                              overflow: "visible",
                              visibility: "visible",
                              opacity: 1,
                            }}
                          >
                            <div
                              className="test-box1"
                              style={{
                                display: "block",
                                width: "100%",
                                height: "auto",
                                maxHeight: "none",
                                overflow: "visible",
                              }}
                            >
                              {extraFeatures.map(
                                (
                                  feature,
                                  index
                                ) => (
                                  <div
                                    className="feature-item"
                                    key={`extra-${index}`}
                                  >
                                    <span className="feature-icon">
                                      <img
                                        src="/images/tickm.png"
                                        alt=""
                                        style={{
                                          width:
                                            "100%",
                                          margin: 0,
                                        }}
                                      />
                                    </span>

                                    <strong>
                                      {feature}
                                    </strong>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* ACTIONS */}

                <div
                  className="col-xs-12 col-sm-4 col-md-4 col-lg-4"
                  style={{ float: "left" }}
                >
                  <div className="actions-container">
                    <div className="car-d">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={openDetailsModal}
                      >
                        {tt(
                          "carDetails",
                          "Car details"
                        )}
                      </button>
                    </div>

                    <button
                      type="button"
                      className="book-btn"
                      onClick={handleGoPrevious}
                    >
                      «{" "}
                      {tt(
                        "goPrevious",
                        "Go Previous"
                      )}
                    </button>
                  </div>
                </div>

                <div className="clearfix"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ======================================================
          IMPORTANT INFORMATION
      ====================================================== */}

      <div className="clearfix"></div>

      <div className="ft-bg">
        <div className="why-choose-us-area">
          <div className="container">
            <div className="row">

              <div
                className="col-md-12"
                align="center"
              >
                <div className="heading-section heading-section-white">
                  {loadingImportantContents ? (
                    <h2
                      className="aboutp"
                      style={{
                        textAlign: "center",
                      }}
                    >
                      {tt(
                        "loading",
                        "Loading..."
                      )}
                    </h2>
                  ) : (
                    <h2
                      className="aboutp"
                      style={{
                        textAlign: "center",
                      }}
                    >
                      {page9Title}
                    </h2>
                  )}
                </div>
              </div>

              {/* PAGE 9 */}

              <div
                className="col-md-6"
                style={{ padding: 0 }}
              >
                <div className="services services-2 w-100">
                  <div className="pr28 text-1 text w-100">
                    <div className="price-card">
                      <div className="content">
                        <h2>{page9Title}</h2>
                      </div>

                      {!loadingImportantContents &&
                        page9Details && (
                          <div
                            className="why-content-page9"
                            dangerouslySetInnerHTML={{
                              __html:
                                page9Details,
                            }}
                          />
                        )}

                      {!loadingImportantContents &&
                        !page9Details && (
                          <p>
                            {tt(
                              "noInformation",
                              "No information available."
                            )}
                          </p>
                        )}

                      <div className="clearfix"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* PAGE 10 + 11 */}

              <div
                className="col-md-6"
                style={{ padding: 0 }}
              >
                <div className="pr28 services services-2 w-100">
                  <div className="price-card1">

                    <div className="content">
                      <h2>{page10Title}</h2>
                    </div>

                    {!loadingImportantContents &&
                      page10Details && (
                        <div
                          className="why-content-page10"
                          dangerouslySetInnerHTML={{
                            __html:
                              page10Details,
                          }}
                        />
                      )}

                    {!loadingImportantContents &&
                      !page10Details && (
                        <p>
                          {tt(
                            "noInformation",
                            "No information available."
                          )}
                        </p>
                      )}

                    <div
                      style={{
                        height: "20px",
                      }}
                    />

                    <div className="content">
                      <h2>{page11Title}</h2>
                    </div>

                    {!loadingImportantContents &&
                      page11Details && (
                        <div
                          className="why-content-page11"
                          dangerouslySetInnerHTML={{
                            __html:
                              page11Details,
                          }}
                        />
                      )}

                    {!loadingImportantContents &&
                      !page11Details && (
                        <p>
                          {tt(
                            "noInformation",
                            "No information available."
                          )}
                        </p>
                      )}

                    <div className="clearfix"></div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ======================================================
          MODERN CAR DETAILS MODAL
      ====================================================== */}

      {showDetailsModal && (
        <div
          className="modern-car-modal-overlay"
          onClick={closeDetailsModal}
        >
          <div
            className="modern-car-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* ==================================================
                MODAL HEADER
            ================================================== */}

            <div className="modern-modal-header">
              <div>
                <div className="modern-modal-small-title">
                  {tt(
                    "vehicleInformation",
                    "VEHICLE INFORMATION"
                  )}
                </div>

                <h2>{carTitle}</h2>

                {carSubtitle && (
                  <p>{carSubtitle}</p>
                )}
              </div>

              <button
                type="button"
                className="modern-modal-close"
                onClick={closeDetailsModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* ==================================================
                MODAL BODY
            ================================================== */}

            <div className="modern-modal-body">

              {/* IMAGE SECTION */}

              <div className="modern-modal-image-section">
                <div className="modern-modal-image-wrapper">
                  <img
                    src={image}
                    alt={carTitle}
                    className="modern-modal-car-image"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src =
                        "/images/car_34.jpg";
                    }}
                  />
                </div>

                {/* PRICE */}

                <div className="modern-price-card">
                  <span>
                    {tt(
                      "pricePerDay",
                      "Price per day"
                    )}
                  </span>

                  <strong>
                    {price !== null
                      ? `€ ${Number(
                          price
                        ).toFixed(2)}`
                      : "N/A"}
                  </strong>
                </div>
              </div>

              {/* INFORMATION */}

              <div className="modern-modal-info">

                {/* QUICK SPECS */}

                <div className="modern-section-title">
                  <span className="modern-section-icon">
                    🚗
                  </span>

                  {tt(
                    "vehicleSpecifications",
                    "Vehicle Specifications"
                  )}
                </div>

                <div className="modern-spec-grid">

                  <ModernSpec
                    icon="👥"
                    label={tt(
                      "passengers",
                      "Passengers"
                    )}
                    value={
                      car.passengers
                        ? `X ${car.passengers}`
                        : "-"
                    }
                  />

                  <ModernSpec
                    icon="🧳"
                    label={tt(
                      "luggage",
                      "Luggage"
                    )}
                    value={
                      car.luggage || "-"
                    }
                  />

                  <ModernSpec
                    icon="🚪"
                    label={tt(
                      "doors",
                      "Doors"
                    )}
                    value={
                      car.doors || "-"
                    }
                  />

                  <ModernSpec
                    icon="⚙️"
                    label={tt(
                      "transmission",
                      "Transmission"
                    )}
                    value={
                      car.transmission || "-"
                    }
                  />

                  <ModernSpec
                    icon="❄️"
                    label={tt(
                      "airConditioning",
                      "A.C."
                    )}
                    value={yesNo(
                      car.airConditioning
                    )}
                  />

                  <ModernSpec
                    icon="🔒"
                    label={tt(
                      "centralLocking",
                      "Central locking"
                    )}
                    value={yesNo(
                      car.centralLocking ??
                        car.central_locking
                    )}
                  />

                </div>

                {/* DETAILS */}

                <div className="modern-section-title modern-details-title">
                  <span className="modern-section-icon">
                    ℹ
                  </span>

                  {tt(
                    "vehicleDetails",
                    "Vehicle Details"
                  )}
                </div>

                <div className="modern-details-grid">

                  <ModernDetail
                    label={tt(
                      "groupName",
                      "Group name"
                    )}
                    value={
                      car.category ||
                      car.category_name
                    }
                  />

                  <ModernDetail
                    label={tt(
                      "brandName",
                      "Brand name"
                    )}
                    value={car.brand}
                  />

                  <ModernDetail
                    label={tt(
                      "modelName",
                      "Model name"
                    )}
                    value={
                      car.model ||
                      car.name
                    }
                  />

                  <ModernDetail
                    label={tt(
                      "engineCapacity",
                      "Engine capacity"
                    )}
                    value={
                      car.engineCapacity ??
                      car.engine_capacity
                    }
                  />

                  <ModernDetail
                    label={tt(
                      "stereo",
                      "Stereo"
                    )}
                    value={yesNo(car.stereo)}
                  />

                  <ModernDetail
                    label={tt(
                      "airbag",
                      "Airbag"
                    )}
                    value={yesNo(car.airbag)}
                  />

                  <ModernDetail
                    label={tt(
                      "electricWindows",
                      "Electric windows"
                    )}
                    value={yesNo(
                      car.electricWindows ??
                        car.electric_windows
                    )}
                  />

                  <ModernDetail
                    label={tt(
                      "driverAge",
                      "Driver age"
                    )}
                    value={
                      car.driverAge ??
                      car.driver_age
                    }
                  />

                </div>

              </div>
            </div>

            {/* ==================================================
                INCLUDED ITEMS
            ================================================== */}

            <div className="modern-included-section">

              <div className="modern-section-title">
                <span className="modern-section-icon">
                  ✓
                </span>

                {tt(
                  "includedFeatures",
                  "Included Features"
                )}
              </div>

              <div className="modern-feature-grid">

                {getAllFeatures(car).length >
                0 ? (
                  getAllFeatures(car).map(
                    (feature, index) => (
                      <div
                        className="modern-feature"
                        key={index}
                      >
                        <span className="modern-check">
                          ✓
                        </span>

                        <span>
                          {feature}
                        </span>
                      </div>
                    )
                  )
                ) : (
                  <div className="modern-no-feature">
                    {tt(
                      "notAvailable",
                      "No additional features available."
                    )}
                  </div>
                )}

              </div>
            </div>

            {/* ==================================================
                FOOTER
            ================================================== */}

            <div className="modern-modal-footer">

              <div className="modern-footer-note">
                <span>🔐</span>

                <span>
                  {tt(
                    "vehicleInfoNote",
                    "Vehicle specifications may vary slightly depending on the actual vehicle provided."
                  )}
                </span>
              </div>

              <button
                type="button"
                className="modern-close-button"
                onClick={closeDetailsModal}
              >
                {tt(
                  "close",
                  "Close"
                )}
              </button>

            </div>

          </div>
        </div>
      )}

      {/* ======================================================
          MODAL CSS
      ====================================================== */}

      <style>{`
        /* =====================================================
           MODERN CAR DETAILS MODAL
        ===================================================== */

        .modern-car-modal-overlay {
          position: fixed;
          inset: 0;
          width: 100%;
          height: 100%;
          background: rgba(10, 20, 35, 0.72);
          backdrop-filter: blur(7px);
          -webkit-backdrop-filter: blur(7px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 25px;
          z-index: 99999;
          animation: modalOverlayFade 0.2s ease;
        }

        @keyframes modalOverlayFade {
          from {
            opacity: 0;
          }

          to {
            opacity: 1;
          }
        }

        .modern-car-modal {
          width: 100%;
          max-width: 1050px;
          max-height: 92vh;
          overflow-y: auto;
          background: #ffffff;
          border-radius: 22px;
          box-shadow:
            0 25px 70px rgba(0, 0, 0, 0.28),
            0 8px 25px rgba(0, 0, 0, 0.12);
          animation: modalSlideUp 0.25s ease;
          position: relative;
          scrollbar-width: thin;
        }

        @keyframes modalSlideUp {
          from {
            opacity: 0;
            transform: translateY(25px) scale(0.98);
          }

          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .modern-car-modal::-webkit-scrollbar {
          width: 7px;
        }

        .modern-car-modal::-webkit-scrollbar-track {
          background: #f1f3f5;
        }

        .modern-car-modal::-webkit-scrollbar-thumb {
          background: #c7ccd3;
          border-radius: 10px;
        }

        /* HEADER */

        .modern-modal-header {
          padding: 26px 32px;
          background: linear-gradient(
            135deg,
            #ffffff 0%,
            #f7f9fc 100%
          );
          border-bottom: 1px solid #e8ebef;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
          position: sticky;
          top: 0;
          z-index: 5;
        }

        .modern-modal-small-title {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.4px;
          color: #7a8491;
          margin-bottom: 5px;
        }

        .modern-modal-header h2 {
          margin: 0;
          color: #17202b;
          font-size: 27px;
          font-weight: 700;
          line-height: 1.25;
        }

        .modern-modal-header p {
          margin: 6px 0 0;
          color: #6b7480;
          font-size: 14px;
        }

        .modern-modal-close {
          width: 42px;
          height: 42px;
          min-width: 42px;
          border: 0;
          border-radius: 50%;
          background: #eef1f5;
          color: #303944;
          font-size: 28px;
          line-height: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modern-modal-close:hover {
          background: #17202b;
          color: #ffffff;
          transform: rotate(90deg);
        }

        /* BODY */

        .modern-modal-body {
          padding: 30px 32px 20px;
          display: grid;
          grid-template-columns: 38% 62%;
          gap: 32px;
        }

        /* IMAGE */

        .modern-modal-image-section {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .modern-modal-image-wrapper {
          width: 100%;
          min-height: 260px;
          background: #f7f8fa;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border: 1px solid #e9edf2;
          padding: 20px;
        }

        .modern-modal-car-image {
          width: 100%;
          max-height: 260px;
          object-fit: contain;
          display: block;
        }

        .modern-price-card {
          border-radius: 15px;
          padding: 17px 20px;
          background: #f7f9fb;
          border: 1px solid #e7ebef;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }

        .modern-price-card span {
          color: #707985;
          font-size: 13px;
          font-weight: 600;
        }

        .modern-price-card strong {
          color: #17202b;
          font-size: 23px;
          font-weight: 750;
        }

        /* SECTION TITLE */

        .modern-section-title {
          display: flex;
          align-items: center;
          gap: 9px;
          font-size: 16px;
          color: #17202b;
          font-weight: 700;
          margin-bottom: 15px;
        }

        .modern-section-icon {
          width: 30px;
          height: 30px;
          border-radius: 9px;
          background: #eef3f8;
          color: #35536f;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 700;
        }

        .modern-spec-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }

        .modern-spec-item {
          padding: 14px;
          border-radius: 13px;
          border: 1px solid #e9edf1;
          background: #ffffff;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .modern-spec-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: #f5f7f9;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 17px;
          flex-shrink: 0;
        }

        .modern-spec-label {
          font-size: 11px;
          color: #8a929d;
          margin-bottom: 2px;
        }

        .modern-spec-value {
          font-size: 13px;
          color: #242c36;
          font-weight: 650;
        }

        .modern-details-title {
          margin-top: 25px;
        }

        .modern-details-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          border-top: 1px solid #edf0f3;
          border-left: 1px solid #edf0f3;
          border-radius: 12px;
          overflow: hidden;
        }

        .modern-detail-item {
          min-height: 52px;
          padding: 11px 14px;
          border-right: 1px solid #edf0f3;
          border-bottom: 1px solid #edf0f3;
          background: #ffffff;
        }

        .modern-detail-label {
          display: block;
          color: #8a929d;
          font-size: 11px;
          margin-bottom: 3px;
        }

        .modern-detail-value {
          display: block;
          color: #242c36;
          font-size: 13px;
          font-weight: 650;
        }

        /* INCLUDED */

        .modern-included-section {
          margin: 5px 32px 0;
          padding: 23px 0;
          border-top: 1px solid #e8ebef;
        }

        .modern-feature-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }

        .modern-feature {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 11px 13px;
          border: 1px solid #e8edf1;
          border-radius: 11px;
          background: #fbfcfd;
          color: #303944;
          font-size: 13px;
          font-weight: 550;
        }

        .modern-check {
          width: 22px;
          height: 22px;
          min-width: 22px;
          border-radius: 50%;
          background: #e9f5ee;
          color: #25804b;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 800;
        }

        .modern-no-feature {
          padding: 15px;
          border-radius: 12px;
          background: #f7f8fa;
          color: #7c848e;
          font-size: 13px;
        }

        /* FOOTER */

        .modern-modal-footer {
          padding: 18px 32px 25px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          border-top: 1px solid #e8ebef;
          background: #ffffff;
        }

        .modern-footer-note {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #7b838e;
          font-size: 11px;
          line-height: 1.5;
          max-width: 650px;
        }

        .modern-close-button {
          border: 0;
          background: #17202b;
          color: #ffffff;
          border-radius: 10px;
          padding: 11px 25px;
          font-size: 13px;
          font-weight: 650;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .modern-close-button:hover {
          background: #303b48;
          transform: translateY(-1px);
        }

        /* RESPONSIVE */

        @media (max-width: 900px) {
          .modern-modal-body {
            grid-template-columns: 1fr;
          }

          .modern-modal-image-wrapper {
            min-height: 220px;
          }

          .modern-feature-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 600px) {
          .modern-car-modal-overlay {
            padding: 10px;
          }

          .modern-car-modal {
            max-height: 95vh;
            border-radius: 16px;
          }

          .modern-modal-header {
            padding: 20px;
          }

          .modern-modal-header h2 {
            font-size: 21px;
          }

          .modern-modal-body {
            padding: 20px;
            gap: 22px;
          }

          .modern-modal-image-wrapper {
            min-height: 180px;
          }

          .modern-spec-grid,
          .modern-details-grid {
            grid-template-columns: 1fr;
          }

          .modern-feature-grid {
            grid-template-columns: 1fr;
          }

          .modern-included-section {
            margin: 5px 20px 0;
          }

          .modern-modal-footer {
            padding: 18px 20px 20px;
            flex-direction: column;
            align-items: stretch;
          }

          .modern-close-button {
            width: 100%;
          }

          .modern-footer-note {
            max-width: 100%;
          }
        }
      `}</style>
    </>
  );
};

// ============================================================
// MODERN SPEC COMPONENT
// ============================================================

function ModernSpec({
  icon,
  label,
  value,
}) {
  return (
    <div className="modern-spec-item">
      <div className="modern-spec-icon">
        {icon}
      </div>

      <div>
        <div className="modern-spec-label">
          {label}
        </div>

        <div className="modern-spec-value">
          {value !== undefined &&
          value !== null &&
          value !== ""
            ? value
            : "-"}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MODERN DETAIL COMPONENT
// ============================================================

function ModernDetail({
  label,
  value,
}) {
  return (
    <div className="modern-detail-item">
      <span className="modern-detail-label">
        {label}
      </span>

      <span className="modern-detail-value">
        {value !== undefined &&
        value !== null &&
        value !== ""
          ? typeof value === "object"
            ? "-"
            : value
          : "-"}
      </span>
    </div>
  );
};

export default CarDetails;